import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWavesurfer } from './useWavesurfer';
import { formatDuration } from './Duration';
import { ProjectContentsSelection } from './ProjectContentsSelection';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import { useParams } from 'react-router-dom';
import {
    getProjectContents,
    patchProjectContentStartsIn,
    patchProjectContentUsability,
} from '../../../../features/sessions/projectsManagement/sessionManageProject';
import { useDragPosition } from './useDragPosition';
import { IconButton } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import mergeIcon from '../../../../assets/merge-icon.png';
import divideIcon from '../../../../assets/divide-icon.png';
import rejectIcon from '../../../../assets/reject.png';
import './waveSurferPlayer.css';
import { toast } from 'react-toastify';
import IconComment from '../../../../assets/comment-icon-white.png';
import IconInfo from '../../../../assets/info-icon-white.png';

export const WaveSurferPlayer = (props: any) => {
    const containerRef         = useRef<any>();
    const containerRefMemorized = useMemo(() => containerRef, [containerRef]);
    // Only pass the URL — prevents WaveSurfer from being destroyed on every re-render
    const wavesurferOptions    = useMemo(() => ({ url: props.url }), [props.url]);
    const wavesurfer           = useWavesurfer(containerRefMemorized, wavesurferOptions);
    const dispatch             = useDispatch<AppDispatch>();
    const { userNicknameRoute, projectIdRoute } = useParams();

    const containerWaveWidth      = props.metadataDurationCentiseconds;
    const projectContentUseful    = props.isUseful;
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const authHeaders                = useSelector((state: RootState) => state.session.authHeaders);
    const containerProjectSketchWidth = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject.project.projectSketchDuration);
    const containerProjectWidth       = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject.project.projectDuration);
    const currentUser                 = useSelector((state: RootState) => state.session.currentUser);
    const currentProject              = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject);

    // Derived selectors — return primitives so each WaveSurferPlayer only re-renders
    // when its own specific slice of state changes, not on every selection change.
    const isSelectionEmpty = useSelector((state: RootState) =>
        state.sessionManageProjectContents.projectContentSelectedList.length === 0
    );
    const isThisItemSelected = useSelector((state: RootState) =>
        state.sessionManageProjectContents.projectContentSelectedList.includes(props.projectContentId)
    );
    const pageLoadedOnce = useSelector((state: RootState) =>
        state.sessionManageProjectContents.pageLoadedOnceTime
    );

    const containerProjectContentOpacity =
        isThisItemSelected || isSelectionEmpty || pageLoadedOnce ? 1 : 0.3;

    const PX_PER_CS = 10;

    const currentUserIsProjectOwner = Boolean(currentUser && currentUser.id === currentProject.project.userId);
    const currentUserIsContentOwner = Boolean(currentUser?.id == props.ownerId);
    const canDrag                   = currentUserIsContentOwner || currentUserIsProjectOwner;

    const maxStartsIn = useMemo(() => {
        if (projectContentUseful === false) {
            return Math.max(0, containerProjectSketchWidth - containerWaveWidth);
        }
        return Math.max(0, containerProjectWidth - containerWaveWidth);
    }, [projectContentUseful, containerProjectSketchWidth, containerProjectWidth, containerWaveWidth]);

    const onCommit = useCallback(async (newStartsIn: number) => {
        await dispatch(patchProjectContentStartsIn({
            authHeaders,
            projectContentsRouteParams: {
                userNicknameRoute,
                projectIdRoute,
            },
            patchProjectContentBody: {
                id: props.projectContentId,
                startsIn: newStartsIn,
            },
        }));
    }, [authHeaders, userNicknameRoute, projectIdRoute, props.projectContentId, dispatch]);

    const { startsInCentiInt, isDragging, handleMouseDown, handleTouchStart } = useDragPosition({
        initialStartsIn: props.startsIn,
        maxStartsIn,
        PX_PER_CS,
        canDrag,
        onCommit,
    });

    const startsInFormated = useMemo(() => formatDuration(startsInCentiInt), [startsInCentiInt]);
    const endsInFormated   = useMemo(() => formatDuration(startsInCentiInt + duration), [startsInCentiInt, duration]);

    const onPlayClick = useCallback(() => {
        wavesurfer?.isPlaying() ? wavesurfer?.pause() : wavesurfer?.play();
    }, [wavesurfer]);

    useEffect(() => {
        containerRefMemorized.current.style.width = '100%';
        if (!wavesurfer) return;

        setCurrentTime(0);
        setIsPlaying(false);

        const subscriptions = [
            wavesurfer.on('play', () => setIsPlaying(true)),
            wavesurfer.on('pause', () => setIsPlaying(false)),
            wavesurfer.on('timeupdate', (currentTime) => {
                setCurrentTime(Math.round(currentTime * 100));
            }),
            wavesurfer.on('ready', () => {
                setDuration(Math.round(wavesurfer.getDuration()) * 100);
            }),
        ];

        return () => {
            subscriptions.forEach((unsub) => unsub());
        };
    }, [wavesurfer]);

    async function handleMoveToUseful() {
        const response = await dispatch(patchProjectContentUsability({
            authHeaders,
            projectContentsRouteParams: { userNicknameRoute, projectIdRoute },
            patchProjectContentBody: { id: props.projectContentId, useful: true },
        }));
        if (response.meta.requestStatus && response.meta.requestStatus !== 'fulfilled') {
            response.payload.errors.map((item: string) => { toast.error(item); });
        } else if (projectIdRoute) {
            await dispatch(getProjectContents({
                authHeaders,
                userNickname: userNicknameRoute,
                projectId: parseInt(projectIdRoute),
            }));
        }
    }

    async function handleMoveToUseless() {
        await dispatch(patchProjectContentUsability({
            authHeaders,
            projectContentsRouteParams: { userNicknameRoute, projectIdRoute },
            patchProjectContentBody: { id: props.projectContentId, useful: false },
        }));
    }

    const containerWidth = projectContentUseful === false
        ? `${containerProjectSketchWidth / PX_PER_CS}px`
        : `${containerProjectWidth / PX_PER_CS}px`;

    const waveContainerClass = `container-wave${isDragging ? ' dragging' : ''}`;

    const renderController = () => (
        <div className="container-controller-box">
            <div className="container-checkBox-play-button">
                <div className="checkBox-container">
                    <ProjectContentsSelection projectContentId={props.projectContentId} />
                </div>
                <IconButton
                    size='small'
                    onClick={onPlayClick}
                    sx={{ color: 'rgba(255,255,255,0.85)', flexShrink: 0 }}
                >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
            </div>
            <div className="container-time-progress">
                <div className="container-time-from-start">
                    <h5>{formatDuration(currentTime)}</h5>
                </div>
                <div className="container-time-to-end">
                    <h5>-{formatDuration(duration - currentTime)}</h5>
                </div>
            </div>
        </div>
    );

    const renderWave = () => (
        <div className="container-wave-slider" style={{ width: containerWidth }}>
            <div
                className={waveContainerClass}
                data-draggable={canDrag ? 'true' : 'false'}
                style={{ width: `${containerWaveWidth / PX_PER_CS}px`, transform: `translateX(${startsInCentiInt / PX_PER_CS}px)` }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div className="wave-surfer" ref={containerRefMemorized} />
            </div>
        </div>
    );

    const renderStartsInEndsIn = () => (
        <div className='container-show-startIn-endsIn'>
            <label className="label-starts-in">Starts In</label>
            <div className="container-starts-in-formated">{startsInFormated}</div>
            <label className="label-starts-in">Ends In</label>
            <div className="container-starts-in-formated">{endsInFormated}</div>
        </div>
    );

    const renderActions = () => (
        isSelectionEmpty ? (
            <div id='container-change-usability-comments-info'>
                <div id='container-change-usability-useless-contents'>
                    {currentUserIsProjectOwner && projectContentUseful === false && (
                        <button id='change-usabilty-button' onClick={handleMoveToUseful}>
                            <img src={mergeIcon} />
                        </button>
                    )}
                    {currentUserIsProjectOwner && projectContentUseful === true && (
                        <button id='change-usabilty-button' onClick={handleMoveToUseless}>
                            <img src={divideIcon} />
                        </button>
                    )}
                    {(currentUserIsContentOwner || currentUserIsProjectOwner) && (
                        <button
                            id='change-usabilty-button'
                            onClick={() => {
                                props.handleSetProjectIdToRemove(props.projectContentId);
                                props.handleShowYesNoModal(true);
                            }}
                        >
                            <img src={rejectIcon} style={{ transform: 'rotate(-90deg) scaleY(-1)' }} />
                        </button>
                    )}
                </div>
                <div id='container-button-comment-info'>
                    <button
                        id='button-comment-project-content'
                        onClick={() => {
                            props.handleShowToggleBoxComments(!props.boxCommentsOpened);
                            props.setProjectContentToComment(props.projectContentId);
                        }}
                    >
                        <img src={IconComment} />
                    </button>
                    <button
                        id='button-project-content-more-info'
                        onClick={() => {
                            props.handleSetShowProjectContenInfoModal(true);
                            props.handleSetIdToShowInfo(props.projectContentId);
                        }}
                    >
                        <img src={IconInfo} />
                    </button>
                </div>
            </div>
        ) : null
    );

    return (
        <div
            className="container-wave-surfer-project"
            style={{
                width: containerWidth,
                opacity: containerProjectContentOpacity,
            }}
        >
            {renderController()}
            {renderWave()}
            {renderStartsInEndsIn()}
            {renderActions()}
        </div>
    );
};
